<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount } from "vue";
import { DataTable } from "simple-datatables";
import axios from "axios";
import { BACK_END_URL } from '@/utilities/config';
import Swal from "sweetalert2";

// State
const logs = ref([]);
const error = ref(null);
const loading = ref(true);
const selectedLog = ref(null);
const showDetailsModal = ref(false);

const openLogDetails = async (cargaId) => {
  try {
    await fetchErrorDetails(cargaId);
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  selectedLog.value = null;
  showDetailsModal.value = false;
};

// Fetch logs from the API
const fetchLogs = async () => {
  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.get(`${BACK_END_URL}/carga-errores/cargas`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    console.log("Fetched logs:", {BACK_END_URL}, response.data); // Debugging log
    

    logs.value = response.data;
  } catch (err) {
    error.value = "Error al cargar los logs.";
    console.error("Error fetching logs:", err);
  } finally {
    loading.value = false;
  }
};
const formatDate = (isoString) => {
  if (!isoString) return 'N/A';

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (e) {
    return 'N/A';
  }
};

// Fetch error details for a specific log
const fetchErrorDetails = async (cargaId) => {
  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.get(`${BACK_END_URL}/carga-errores`, {
      params: { carga_id: cargaId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    selectedLog.value = {
      ...logs.value.find(log => log.id === cargaId),
      errors: response.data
    };
    showDetailsModal.value = true;

  } catch (err) {
    if (err.response && err.response.status === 404) {
      // Handle case where no errors were found
      selectedLog.value = {
        ...logs.value.find(log => log.id === cargaId),
        errors: []
      };
      showDetailsModal.value = true;


    } else {
      // Handle other errors
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los detalles del log",
        icon: "error"
      });
      console.error("Error fetching error details:", err);
    }
  }
};

const downloadErrorCSV = async (cargaId) => {
  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.get(`${BACK_END_URL}/carga-errores/csv`, {
      params: { carga_id: cargaId },
      headers: {
        Authorization: `Bearer ${token}`
      },
      responseType: 'blob' // Important for file downloads
    });

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `errores_carga_${cargaId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (err) {
    Swal.fire({
      title: "Error",
      text: "No se pudo descargar el archivo CSV",
      icon: "error"
    });
    console.error("Error downloading CSV:", err);
  }
};

onMounted(async () => {
  await fetchLogs();
  await nextTick();
  setupDataTable();
});

const setupDataTable = () => {
  const tableElement = document.getElementById("logs-list");
  if (!tableElement) return;

  // Destroy existing DataTable if it exists
  if (tableElement._datatable) {
    tableElement._datatable.destroy();
  }

  // Initialize new DataTable
  const dataTable = new DataTable(tableElement, {
    searchable: true,
    fixedHeight: true,
    perPage: 10,
    labels: {
      placeholder: "Buscar registro...",
      perPage: "{select} registros por página",
      noRows: "No se encontraron registros",
      info: "Mostrando {start} al {end} de {rows} registros",
    },
  });

  // Store reference to DataTable instance
  tableElement._datatable = dataTable;

  // Add event delegation to document instead of tableElement
  document.addEventListener('click', handleTableClick);
};

const handleTableClick = (event) => {
  const row = event.target.closest('tr');
  const tableElement = document.getElementById("logs-list");
  if (!row || !tableElement || !tableElement.contains(row)) return;

  // Skip header row clicks
  if (row.classList.contains('datatable-head')) return;

  // Get the row index (accounting for header row)
  const rows = Array.from(tableElement.querySelectorAll('tbody tr'));
  const rowIndex = rows.indexOf(row);

  if (rowIndex >= 0 && rowIndex < logs.value.length) {
    openLogDetails(logs.value[rowIndex].id);
  }
};

onBeforeUnmount(() => {
  // Clean up event listener
  document.removeEventListener('click', handleTableClick);
});
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row mt-4">
      <div v-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-else-if="loading" class="text-center">
        <p>Cargando registros...</p>
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <div v-else class="card">
        <div class="p-3 pb-0 card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="mb-2">Historial de Cargas</h6>
          </div>
        </div>

        <div class="table-responsive pb-4">
          <table id="logs-list" class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7"> ID</th>
                <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ps-2">Archivo</th>
                <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ps-2">Fecha</th>
                <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ps-2">Errores</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id" class="row-hover">
                <td class="pe-2 py-1">
                  <span class="text-xs font-weight-bold">{{ log.id }}</span>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">{{ log.nombre_archivo }}</p>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs text-secondary mb-0">
                    {{ formatDate(log.fecha) }}

                  </p>
                </td>
                <td class="px-2 py-1">
                    <span class="badge" :class="{
                    'bg-danger': log.errores >= 1,
                    'bg-success': log.errores < 1
                    }">
                    {{ log.errores }}
                    </span>
                </td>
                <td class="px-2 py-1">
                  <button class="btn btn-sm my-1 btn-outline-info me-1" @click="openLogDetails(log.id)"
                    title="Ver detalles">
                    <i class="fas fa-eye"></i> Ver detalles
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Log Details Modal -->
    <div v-if="selectedLog" class="modal-overlay" @click="closeModal">
      <div class="modal-content card" @click.stop style="max-width: 800px;">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Detalles del Registro #{{ selectedLog.id }}</h5>
          <button class="btn-close" @click="closeModal"></button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <p class="text-sm"><strong>Archivo:</strong> {{ selectedLog.nombre_archivo }}</p>
              <p class="text-sm"><strong>Fecha:</strong> {{ formatDate(selectedLog.fecha) }}</p>
              <p class="text-sm"><strong>Total Errores:</strong>
                <span class="badge ms-2" :class="{
                    'bg-danger': selectedLog.errors.length >= 1,
                    'bg-success': selectedLog.errors.length < 1
                    }">
                    {{ selectedLog.errors.length }}
                    </span>
              </p>
            </div>
          </div>

          <hr class="horizontal dark my-3">

          <h6 class="mb-3 d-flex justify-content-between">Detalles de Errores
            <button 
    v-if="selectedLog.errors.length > 0"
    class="btn btn-sm btn-outline-success ms-3 m-0"
    @click="downloadErrorCSV(selectedLog.id)"
    title="Descargar CSV"
  >
    <i class="fas fa-download me-1"></i> Descargar CSV
  </button>
          </h6>
          <div v-if="selectedLog.errors.length === 0" class="alert alert-light">
            <i class="fas fa-info-circle me-2"></i>
            No se encontraron errores para esta carga
          </div>
          <div v-else class="table-responsive" style="max-height: 400px; overflow-y: auto;">
            <table class="table align-items-start mb-0">
              <thead>
                <tr>
                  <th class="ps-1">Línea</th>
                  <th class="ps-1">Cédula</th>
                  <th class="ps-1">Mensaje de Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(error, idx) in selectedLog.errors" :key="idx">
                  <td class="text-xs">{{ error.linea }}</td>
                  <td class="text-xs">{{ error.cedula || 'N/A' }}</td>
                  <td class="text-xs text-danger">{{ error.mensaje }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.row-hover {
  transition: background-color 0.3s ease;
}

.row-hover:hover {
  background-color: #f0f4f8;
}

.table-responsive {
  margin-top: 20px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  margin: 2rem auto;
  color: #f0f4f8;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  /* Asegura que la "X" tenga un color visible */
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
  /* Cambia de color al pasar el cursor, si lo deseas */
}
</style>
